import { useState, useEffect } from 'react'
import { supabase } from './supabase'
import { getCustomerByPhone, CustomerProfile } from './customer'

export function useCustomerLookup(
  phone: string,
  restaurantId: string | number | undefined,
) {
  const [customer, setCustomer] = useState<CustomerProfile | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!phone || phone.length < 8 || !restaurantId) {
      setCustomer(null)
      return
    }

    const timer = setTimeout(async () => {
      setLoading(true)
      try {
        const result = await getCustomerByPhone(phone)
        setCustomer(result)
      } catch (err) {
        setCustomer(null)
      } finally {
        setLoading(false)
      }
    }, 400)

    return () => clearTimeout(timer)
  }, [phone, restaurantId])

  return { customer, loading }
}

export function useLoyaltySummary(
  phone: string,
  restaurantId: string | number | undefined,
) {
  const [summary, setSummary] = useState<any>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!phone || phone.length < 8 || !restaurantId) {
      setSummary(null)
      return
    }

    async function fetchSummary() {
      setLoading(true)
      const { data } = await supabase
        .from('v_loyalty_customer_summary')
        .select('*')
        .eq('phone', phone)
        .eq('restaurant_id', restaurantId)
        .single()

      setSummary(data || null)
      setLoading(false)
    }

    fetchSummary()
  }, [phone, restaurantId])

  return { summary, loading }
}

export function useAvailableRewards(
  phone: string,
  restaurantId: string | number | undefined,
) {
  const [rewards, setRewards] = useState<any[]>([])
  const [loading, setLoading] = useState(false)

  const fetchRewards = async () => {
    if (!phone || phone.length < 8 || !restaurantId) {
      setRewards([])
      return
    }
    setLoading(true)
    const { data } = await supabase.rpc('get_available_rewards_by_phone', {
      p_restaurant_id: restaurantId,
      p_phone: phone,
    })
    setRewards(data || [])
    setLoading(false)
  }

  useEffect(() => {
    fetchRewards()
  }, [phone, restaurantId])

  return { rewards, loading, refetch: fetchRewards }
}

export function useRedeemReward() {
  const [loading, setLoading] = useState(false)

  const redeem = async (rewardId: string) => {
    setLoading(true)
    try {
      const { error } = await supabase.rpc('redeem_loyalty_reward', {
        p_customer_loyalty_reward_id: rewardId,
        p_redeemed_on_order_id: null,
      })
      if (error) throw error
      return { success: true }
    } catch (err: any) {
      console.error('Error redeeming reward:', err)
      return { success: false, error: err.message }
    } finally {
      setLoading(false)
    }
  }

  return { redeem, loading }
}

export function useLoyaltyDashboard(restaurantId: string | number | undefined) {
  const [customers, setCustomers] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!restaurantId) return

    async function fetchDashboard() {
      setLoading(true)
      const { data } = await supabase
        .from('v_loyalty_customer_summary')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .order('total_spent', { ascending: false })

      setCustomers(data || [])
      setLoading(false)
    }

    fetchDashboard()
  }, [restaurantId])

  return { customers, loading }
}
